import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SPECIALITIES, TITLES } from 'src/app/core/data/app.data';
import { GroupService } from './../../../../core/services/group.service';
import { ThemeService } from './../../../../core/services/theme.service';
import { UserService } from './../../../../core/services/user.service';
import { SignaturePadComponent } from './../../../../shared/components/signature-pad/signature-pad.component';

/**
 * The refusal codes `PUT /api/profile` returns for the two Lancet delivery-preference fields.
 * Kept as a const rather than an enum so the compiled shape is a plain string the verification
 * harness can drive without importing Angular.
 */
export const LANCET_DELIVERY_ERROR_CODES = {
  NOT_A_MEMBER: 'LANCET_DELIVERY_GROUP_NOT_A_MEMBER',
  GROUP_INVALID: 'LANCET_DELIVERY_GROUP_INVALID',
  ENABLED_INVALID: 'LANCET_DELIVERY_ENABLED_INVALID',
  ENABLED_CONFLICT: 'LANCET_DELIVERY_ENABLED_CONFLICT',
} as const;

/** The on/off choices kept in `User.settings`. A choice never saved counts as off. */
const USER_SETTINGS = ['ccMeInCommunication', 'calendarAllPractices'] as const;

/** The whole settings object to save, or null when none of the choices changed. */
export function changedSettings(stored: any, values: Record<string, any>): Record<string, any> | null {
  const changes: Record<string, any> = {};
  for (const key of USER_SETTINGS) {
    const value = values[key] === true;
    if (value !== (stored?.[key] === true)) changes[key] = value;
  }
  return Object.keys(changes).length ? { ...(stored || {}), ...changes } : null;
}

/** Shown when the request failed without a recognised `code` — network, 500, or an old server. */
export const GENERIC_SAVE_ERROR =
  'There was an error updating your profile. Nothing was saved. Please try again.';

@Component({
  selector: 'app-edit-profile-dialog',
  templateUrl: './edit-profile-dialog.component.html',
  styleUrls: ['./edit-profile-dialog.component.scss']
})
export class EditProfileDialogComponent implements OnInit, OnDestroy {

  @ViewChild(SignaturePadComponent) signaturePadComponent!: SignaturePadComponent;

  profileForm!: FormGroup;

  titles = TITLES;
  countries = [{ text: 'South Africa', value: 'South Africa' }];
  specialities = SPECIALITIES;
  initialData: any;
  signatureImageUrl?: string;

  /**
   * The practices the signed-in user actually belongs to. Read from the list the app already
   * holds (`GroupService.userGroups$`, filled by `getUserGroups()` at sign-in) rather than
   * re-fetching: calling `getUserGroups()` again would also re-pick the selected group and can
   * navigate away, which is not something opening a profile dialog should do.
   */
  userGroups: { id: string; name: string }[] = [];

  /**
   * A `lancetDeliveryGroupId` is stored that is not in `userGroups` any more — the user left
   * that practice after saving the setting. The server re-validates membership on every inbound
   * result, so the setting is already inert; the select must say so rather than render blank.
   */
  savedDeliveryGroupMissing = false;

  /** The stored value, kept so the "no longer a member" option has something to bind to. */
  savedDeliveryGroupId: string | null = null;

  /**
   * The account toggle is held OFF until this is dismissed. Turning it on takes results away
   * from the user's other account, so the consequence is stated before the switch moves — not
   * in a toast once it already has.
   */
  showLancetDeliveryConfirm = false;

  /**
   * In flight. Mirrors `LoginComponent.isLoggingIn`: the button is disabled AND its label
   * changes, because the only other progress indicator is a 4px bar at the top of the viewport
   * that users miss and then click Save again.
   */
  isSaving = false;

  /** Mapped, human copy for the failure that kept the dialog open. Empty when there is none. */
  saveError = '';

  private groupsSub?: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private themeService: ThemeService,
    private groupService: GroupService,
    private userService: UserService,
    public dialogRef: MatDialogRef<EditProfileDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.profileForm = this.formBuilder.group({
      userType: ['', Validators.required],
      title: ['', Validators.required],
      name: ['', Validators.required],
      surname: ['', Validators.required],
      country: ['', Validators.required],
      speciality: ['', Validators.required],
      mpNumber: ['', Validators.required],
      qualifications: [''],
      yearQualified: [''],
      idNumber: [''],
      email: ['', [Validators.required, Validators.email]],
      signature: [''],
      preferredTheme: ['darkMode'],
      existingSignature: [''],
      suppressConsent: [false],
      disableAutoDownload: [false],
      lancetCode: [''],
      ccMeInCommunication: [false],
      calendarAllPractices: [false],
      // Both are SELF-ONLY on the server: a request naming another `userId` and carrying either
      // is refused. They therefore live here, on the signed-in user's own profile, and are
      // deliberately absent from `shared/components/lab-order-config-dialog`, which is where a
      // practice manager fills in a colleague's `lancetCode`.
      lancetDeliveryGroupId: [null as string | null],
      lancetDeliveryEnabled: [false],
    });

    data.preferredTheme = data?.preferredTheme || 'darkMode';

    this.profileForm.patchValue(data);
    this.profileForm.patchValue({
      ccMeInCommunication: data?.settings?.ccMeInCommunication ?? false,
      calendarAllPractices: data?.settings?.calendarAllPractices ?? false,
    });

    // Normalise both fields to the two shapes the server accepts BEFORE snapshotting, so the
    // change diff in `buildChangedFields()` compares like with like. Without this a profile that
    // simply has no value yet (`undefined`) differs from the control's `null` and every save
    // would carry a pointless `lancetDeliveryGroupId: null`.
    const savedGroupId: string | null = data?.lancetDeliveryGroupId ?? null;
    const savedEnabled: boolean = data?.lancetDeliveryEnabled === true;
    this.savedDeliveryGroupId = savedGroupId;
    this.profileForm.patchValue({
      lancetDeliveryGroupId: savedGroupId,
      lancetDeliveryEnabled: savedEnabled,
    });

    this.signatureImageUrl = data.signatureImageUrl;
    this.initialData = {
      ...data,
      lancetDeliveryGroupId: savedGroupId,
      lancetDeliveryEnabled: savedEnabled,
    }; // Store initial data for comparison
    this.configureFormValidation(this.profileForm.controls);
  }

  ngOnInit(): void {
    this.profileForm.get('userType')!.valueChanges
      .pipe(
    ).subscribe(() => {
      this.configureFormValidation(this.profileForm.controls);
    });

    this.groupsSub = this.groupService.userGroups$.subscribe((groups: any) => {
      this.userGroups = Array.isArray(groups)
        ? groups.map((group: any) => ({
          id: String(group?.id ?? ''),
          name: String(group?.name ?? 'Unnamed practice'),
        }))
        : [];
      this.savedDeliveryGroupMissing = !!this.savedDeliveryGroupId
        && !this.userGroups.some(group => group.id === this.savedDeliveryGroupId);
    });
  }

  ngOnDestroy(): void {
    this.groupsSub?.unsubscribe();
  }

  /**
   * Whether to offer the account toggle.
   *
   * The server does NOT tell us whether another account shares this user's HPCSA number, and
   * there is no honest way to work that out in the browser — so this deliberately does not try.
   * The choice is to show it to every doctor who has an HPCSA number on file, because:
   *
   *  - hiding it unless it is already on would make it impossible to ever turn on, which would
   *    leave the whole account half of the feature unreachable from the UI;
   *  - the field it keys off is `mpNumber`, so a doctor without one cannot be affected by it and
   *    is spared the control entirely — that is not duplicate detection, just relevance;
   *  - for a doctor with a single account, switching it on is a no-op: nothing else shares the
   *    HPCSA, so nothing is disabled anywhere and routing is unchanged.
   *
   * The copy carries the precondition instead of the visibility rule.
   */
  get showLancetDeliverySection(): boolean {
    return this.profileForm.get('userType')?.value === 'doctor';
  }

  get showLancetDeliveryToggle(): boolean {
    if (!this.showLancetDeliverySection) {
      return false;
    }
    // `mpNumber` is disabled, so read it off the control rather than `profileForm.value`.
    return String(this.profileForm.get('mpNumber')?.value ?? '').trim() !== '';
  }

  /**
   * Intercepts the switch. Turning it OFF is safe and takes effect immediately; turning it ON
   * takes the user's Lancet results away from their other account, so the switch is put straight
   * back to off and a confirmation is shown. Nothing is committed until they say yes AND save.
   */
  onLancetDeliveryEnabledChange(event: MatSlideToggleChange): void {
    // Both branches write the control explicitly. `formControlName` has normally written it
    // already by the time `(change)` fires, but leaning on that ordering would make the OFF path
    // depend on an implementation detail of the toggle's value accessor.
    const control = this.profileForm.get('lancetDeliveryEnabled')!;
    if (!event.checked) {
      control.setValue(false, { emitEvent: false });
      this.showLancetDeliveryConfirm = false;
      return;
    }
    control.setValue(false, { emitEvent: false });
    this.showLancetDeliveryConfirm = true;
  }

  confirmLancetDeliveryEnabled(): void {
    this.profileForm.get('lancetDeliveryEnabled')!.setValue(true, { emitEvent: false });
    this.showLancetDeliveryConfirm = false;
  }

  cancelLancetDeliveryEnabled(): void {
    this.profileForm.get('lancetDeliveryEnabled')!.setValue(false, { emitEvent: false });
    this.showLancetDeliveryConfirm = false;
  }

  configureFormValidation(controls: { [key: string]: AbstractControl; }) {
    if (controls['userType'].value === 'doctor') {
      controls['mpNumber'].setValidators([Validators.required]);
      controls['speciality'].setValidators([Validators.required]);
    } else {
      controls['mpNumber'].clearValidators();
      controls['speciality'].clearValidators();
    }

    controls['mpNumber'].updateValueAndValidity();
    controls['speciality'].updateValueAndValidity();
    controls['mpNumber'].disable();
    this.profileForm.updateValueAndValidity();

  }

  previewTheme(event: MatSelectChange) {
    this.themeService.applyTheme(event.value);
  }

  getSignatureData(base64Image: any) {
    // Store the base64 image directly in the form
    this.profileForm.patchValue({
      signature: base64Image
    });
  }

  clearSignature() {
    this.signatureImageUrl = '';
    this.profileForm.patchValue({ existingSignature: '' });
  }

  /**
   * Builds the changed-fields payload. Same diff-against-`initialData` shape the dialog has
   * always used — `PUT /api/profile` reads every field with `!== undefined`, so a key that is
   * absent means "leave it alone".
   *
   * The two delivery fields are normalised on the way out, and the normalisation matters:
   *
   *  - a cleared practice must go as `null`, NOT `undefined`. `undefined` is "no change" to the
   *    server, so sending it would make the setting impossible to ever turn off — the doctor
   *    would clear the select, save, see success, and still have results routed to the old
   *    practice.
   *  - the toggle must go as a real boolean. The server does not coerce (`Boolean('false')` is
   *    `true`), and refuses anything else with `LANCET_DELIVERY_ENABLED_INVALID`.
   *
   * Neither field is ever included for a non-doctor: a Lancet code is not valid for admin or
   * practice-support accounts, so a delivery preference for one is meaningless.
   */
  buildChangedFields(): { [key: string]: any } {
    const updatedUser: { [key: string]: any } = {};
    const isDoctor = this.profileForm.get('userType')?.value === 'doctor';

    const settings = changedSettings(this.initialData?.settings, this.profileForm.value);
    if (settings) {
      updatedUser['settings'] = settings;
    }

    Object.keys(this.profileForm.value).forEach(key => {
      if (key === 'ccMeInCommunication' || key === 'calendarAllPractices') {
        return;
      } else if (key === 'lancetDeliveryGroupId') {
        if (!isDoctor) { return; }
        const current: string | null = this.profileForm.value[key] ?? null;
        if (current !== (this.initialData?.lancetDeliveryGroupId ?? null)) {
          updatedUser[key] = current;
        }
      } else if (key === 'lancetDeliveryEnabled') {
        if (!isDoctor) { return; }
        const current: boolean = this.profileForm.value[key] === true;
        if (current !== (this.initialData?.lancetDeliveryEnabled === true)) {
          updatedUser[key] = current;
        }
      } else if (this.profileForm.value[key] !== this.initialData[key]) {
        updatedUser[key] = this.profileForm.value[key];
      }
    });

    return updatedUser;
  }

  /**
   * Specific copy per refusal `code`. A generic "could not save" next to a practice picker tells
   * the doctor nothing about which of their choices the server would not take, and for
   * `LANCET_DELIVERY_GROUP_NOT_A_MEMBER` the server names the group in `details` precisely so we
   * do not have to guess.
   *
   * Returns '' when the code is not one of ours, so the caller can fall back.
   */
  messageForFailureCode(code: string, details?: any): string {
    switch (code) {
      case LANCET_DELIVERY_ERROR_CODES.NOT_A_MEMBER: {
        const rejectedId = details?.lancetDeliveryGroupId
          ? String(details.lancetDeliveryGroupId)
          : '';
        const match = this.userGroups.find(group => group.id === rejectedId);
        const practice = match ? `"${match.name}"` : 'the practice you chose';
        return `Your Lancet results cannot be delivered to ${practice}: you are not an active `
          + 'member of that practice. Your practice memberships may have changed since this '
          + 'dialog was opened. Nothing was saved — choose a practice you currently work at, or '
          + 'choose "No preference".';
      }
      case LANCET_DELIVERY_ERROR_CODES.GROUP_INVALID:
        return 'The practice we sent was not a recognisable practice id, so nothing was saved. '
          + 'Close this dialog, reopen it, and choose your delivery practice again. If it keeps '
          + 'happening, please contact support.';
      case LANCET_DELIVERY_ERROR_CODES.ENABLED_INVALID:
        return 'The "deliver my Lancet results to this account" setting was not sent as a plain '
          + 'on or off, so nothing was saved. Close this dialog, reopen it, and set it again. If '
          + 'it keeps happening, please contact support.';
      case LANCET_DELIVERY_ERROR_CODES.ENABLED_CONFLICT:
        return 'Another account that shares your HPCSA number switched Lancet delivery on at the '
          + 'same moment. Only one account can receive your results, so nothing was saved here. '
          + 'Turn delivery off on that account first, then try again.';
      default:
        return '';
    }
  }

  /**
   * Nothing was written: every one of these refusals returns before the row is saved. So the
   * dialog stays open with the reason, the two routing fields go back to what the server
   * actually holds, and the cached profile is re-read.
   *
   * That last step is not optional. `UserService.updateUserProfile()` pushes the outgoing values
   * into `userProfile$` OPTIMISTICALLY, before the request resolves — so without a re-read the
   * settings page behind this dialog would keep showing the rejected practice as though it had
   * stuck.
   */
  handleSaveFailure(error?: any, fallbackMessage?: string): void {
    const body = error?.error ?? {};
    const code = typeof body?.code === 'string' ? body.code : '';
    this.saveError = this.messageForFailureCode(code, body?.details)
      || fallbackMessage
      || GENERIC_SAVE_ERROR;

    this.profileForm.get('lancetDeliveryGroupId')!
      .setValue(this.initialData?.lancetDeliveryGroupId ?? null, { emitEvent: false });
    this.profileForm.get('lancetDeliveryEnabled')!
      .setValue(this.initialData?.lancetDeliveryEnabled === true, { emitEvent: false });
    this.showLancetDeliveryConfirm = false;

    this.userService.getUserProfile().subscribe({ next: () => { }, error: () => { } });
  }

  onSubmit() {
    // Already saving — ignore the extra submit. Covers a double-click and an impatient second
    // click during the 200ms signature settle plus the round trip.
    if (this.isSaving) {
      return;
    }
    if (!this.profileForm.valid) {
      return;
    }

    if (this.signaturePadComponent?.isSignatureDrawn()) {
      this.profileForm.patchValue({
        signature: this.signaturePadComponent.getPadData()
      });
    } else if (this.signatureImageUrl) {
      this.profileForm.patchValue({
        existingSignature: this.signatureImageUrl
      });
    } else {
      this.profileForm.patchValue({
        signature: undefined
      });
    }

    this.isSaving = true;
    this.saveError = '';

    setTimeout(() => {
      const updatedUser = this.buildChangedFields();

      // Early return: nothing changed, so there is nothing to PUT. The flag has to be cleared
      // here too — a Save button that can never be pressed again is worse than the extra request.
      if (Object.keys(updatedUser).length === 0) {
        this.isSaving = false;
        this.dialogRef.close({ saved: false });
        return;
      }

      this.userService.updateUserProfile({ id: this.data?.id, ...updatedUser })
        .pipe(
          // Cleared on success, on error and on unsubscribe, for the same reason the login
          // button does it here: any path that terminates without calling either handler would
          // otherwise leave the dialog permanently unsavable.
          finalize(() => { this.isSaving = false; })
        )
        .subscribe({
          next: (response: any) => {
            if (response?.success) {
              this.dialogRef.close({ saved: true });
            } else {
              // 200 with `success: false` — the profile handler uses this shape too.
              this.handleSaveFailure({ error: response }, response?.message);
            }
          },
          error: (error: any) => {
            this.handleSaveFailure(error);
          },
        });
    }, 200);
  }
}